const SWITCHGEAR_COUNTER_TABLE_TYPE = 'switchgear-counter';
const ON_PREM_SWITCHGEAR_ENTITY_ID = 'default';

const shouldKeepAnalyticsTableComponentScope = () => {
  // Add future analytics table types here if one of them should keep component scope.
  // Current table panels do not support component-scoped filtering.
  return false;
};

const getSwitchgearCounterSources = (sources = []) =>
  sources.filter((source) => source.type === 'dec');

const getSwitchgearCounterSourceOptions = (sources = []) =>
  getSwitchgearCounterSources(sources).map((source) => ({
    enum: [source.id],
    title: source.name,
  }));

const getDefaultSwitchgearCounterSourceId = (sources = [], isOnPremise = false) => {
  if (isOnPremise) {
    return null;
  }

  const switchgearSources = getSwitchgearCounterSources(sources);
  return switchgearSources.length === 1 ? switchgearSources[0].id : null;
};

const getRequiredSwitchgearCounterSourceFields = (sources = [], isOnPremise = false) => {
  if (isOnPremise) {
    return [];
  }

  return getSwitchgearCounterSourceOptions(sources).length > 0 ? ['sourceId'] : [];
};

const shouldShowAnalyticsTablePanelType = (sources = [], isOnPremise = false) =>
  isOnPremise || getSwitchgearCounterSources(sources).length > 0;

const isSwitchgearCounterSourceEligible = (source) => {
  const switchgearCounterOptions = source?.options?.analytics?.swgCounter;

  return (
    switchgearCounterOptions?.enabled === true && Boolean(switchgearCounterOptions.importedFileName)
  );
};

// Cloud panels can target a specific EC source. On-prem never needs a source selection
// because the API expects the fixed "default" entity_id instead.
const resolveSwitchgearCounterSource = ({ sources = [], sourceId, isOnPremise = false }) => {
  if (isOnPremise) {
    return null;
  }

  const switchgearSources = getSwitchgearCounterSources(sources);
  if (sourceId) {
    return switchgearSources.find((source) => source.id === sourceId) || null;
  }

  return switchgearSources.length === 1 ? switchgearSources[0] : null;
};

// The analytics API always requires entity_id. In cloud that comes from the selected EC
// source tenant, while on-prem always uses the fixed "default" value.
// The switchgear-counter-aggregates API is always source-scoped, so a real source id is
// still required on-prem even though there is no user-facing source selection there — it
// resolves to the site's single identiq-ts source instead of a user-selected EC source.
const getSwitchgearCounterSourceContext = ({ sources = [], sourceId, isOnPremise = false }) => {
  const selectedSource = resolveSwitchgearCounterSource({ sources, sourceId, isOnPremise });
  const onPremSource = isOnPremise
    ? (sources.find((source) => source.type === 'identiq-ts') ?? null)
    : null;
  const eligibleSource = isOnPremise ? onPremSource : selectedSource;

  return {
    selectedSource,
    resolvedSourceId: isOnPremise ? (onPremSource?.id ?? null) : (selectedSource?.id ?? null),
    entityId: isOnPremise ? ON_PREM_SWITCHGEAR_ENTITY_ID : selectedSource?.options?.tenant || null,
    isEligible: isSwitchgearCounterSourceEligible(eligibleSource),
  };
};

const getSwitchgearCounterEntityId = ({ sources = [], sourceId, isOnPremise = false }) => {
  return getSwitchgearCounterSourceContext({ sources, sourceId, isOnPremise }).entityId;
};

const normalizeAnalyticsTableSettings = ({
  tableType,
  settings = {},
  sources = [],
  isOnPremise = false,
}) => {
  if (tableType !== SWITCHGEAR_COUNTER_TABLE_TYPE) {
    return settings;
  }

  // When there is exactly one EC source in cloud, we prefill the required Source field
  // so the panel can be configured without an unnecessary extra selection step.
  const defaultSourceId = getDefaultSwitchgearCounterSourceId(sources, isOnPremise);
  if (!defaultSourceId || settings.sourceId) {
    return settings;
  }

  return {
    ...settings,
    sourceId: defaultSourceId,
  };
};

export {
  SWITCHGEAR_COUNTER_TABLE_TYPE,
  ON_PREM_SWITCHGEAR_ENTITY_ID,
  shouldKeepAnalyticsTableComponentScope,
  getSwitchgearCounterSources,
  getSwitchgearCounterSourceOptions,
  getDefaultSwitchgearCounterSourceId,
  getRequiredSwitchgearCounterSourceFields,
  shouldShowAnalyticsTablePanelType,
  resolveSwitchgearCounterSource,
  getSwitchgearCounterSourceContext,
  getSwitchgearCounterEntityId,
  normalizeAnalyticsTableSettings,
};
